const REFRESH_LOCK_KEY = 'ucomm_auth_refresh_lock';
const REFRESH_LOCK_MS = 15_000;
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

const authChannel =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('ucomm-auth-tokens') : null;

export type AuthTokenSyncMessage =
  | { type: 'tokens-updated' }
  | { type: 'tokens-cleared' };

/** Prevent parallel refresh calls across tabs (rotation revokes the old refresh token). */
export function tryAcquireRefreshLock(): boolean {
  try {
    const raw = localStorage.getItem(REFRESH_LOCK_KEY);
    const now = Date.now();
    if (raw) {
      const started = Number.parseInt(raw, 10);
      if (Number.isFinite(started) && now - started < REFRESH_LOCK_MS) {
        return false;
      }
    }
    localStorage.setItem(REFRESH_LOCK_KEY, String(now));
    return true;
  } catch {
    return true;
  }
}

export function releaseRefreshLock(): void {
  try {
    localStorage.removeItem(REFRESH_LOCK_KEY);
  } catch {
    /**/
  }
}

/** Wait until another tab finishes refresh and writes new tokens. */
export function waitForTokenUpdateFromOtherTab(
  previousRefreshToken: string | null,
  timeoutMs = 12_000,
): Promise<boolean> {
  return new Promise((resolve) => {
    const deadline = Date.now() + timeoutMs;

    const finish = (ok: boolean) => {
      window.removeEventListener('storage', onStorage);
      authChannel?.removeEventListener('message', onMessage);
      resolve(ok);
    };

    const tokensChanged = (): boolean => {
      const refresh = localStorage.getItem(REFRESH_TOKEN_KEY);
      const access = localStorage.getItem(ACCESS_TOKEN_KEY);
      if (!refresh || !access) return false;
      return refresh !== previousRefreshToken;
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key !== ACCESS_TOKEN_KEY && event.key !== REFRESH_TOKEN_KEY) return;
      if (tokensChanged()) finish(true);
    };

    const onMessage = (event: MessageEvent<AuthTokenSyncMessage>) => {
      const data = event.data;
      if (data?.type === 'tokens-updated' && tokensChanged()) finish(true);
      if (data?.type === 'tokens-cleared') finish(false);
    };

    window.addEventListener('storage', onStorage);
    authChannel?.addEventListener('message', onMessage);

    const poll = () => {
      if (tokensChanged()) {
        finish(true);
        return;
      }
      if (Date.now() >= deadline) {
        finish(false);
        return;
      }
      window.setTimeout(poll, 200);
    };
    poll();
  });
}

export function notifyTokensUpdated(): void {
  authChannel?.postMessage({ type: 'tokens-updated' } satisfies AuthTokenSyncMessage);
}

export function notifyTokensCleared(): void {
  authChannel?.postMessage({ type: 'tokens-cleared' } satisfies AuthTokenSyncMessage);
}

export function subscribeAuthTokenSync(
  handler: (message: AuthTokenSyncMessage) => void,
): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === REFRESH_TOKEN_KEY && event.newValue == null) {
      handler({ type: 'tokens-cleared' });
      return;
    }
    if (
      (event.key === ACCESS_TOKEN_KEY || event.key === REFRESH_TOKEN_KEY) &&
      event.newValue != null
    ) {
      handler({ type: 'tokens-updated' });
    }
  };

  const onMessage = (event: MessageEvent<AuthTokenSyncMessage>) => {
    if (event.data?.type) handler(event.data);
  };

  window.addEventListener('storage', onStorage);
  authChannel?.addEventListener('message', onMessage);

  return () => {
    window.removeEventListener('storage', onStorage);
    authChannel?.removeEventListener('message', onMessage);
  };
}

export function readTokensFromStorage(): { access_token: string; refresh_token: string } | null {
  const access_token = localStorage.getItem(ACCESS_TOKEN_KEY);
  const refresh_token = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!access_token || !refresh_token) return null;
  return { access_token, refresh_token };
}
